import type {
  CreateValidationRequestType,
  Validation,
  ValidationAttemptsExceeded,
} from "@/api/generated/model";
import {
  createValidation,
  getValidation,
} from "@/api/generated/validations/validations";
import { startCourseCompletionQuiz } from "@/api/generated/course-completion-quiz/course-completion-quiz";
import { HttpError, errorCodeOf } from "@/api/apiFetch";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { ValidationAttemptsExceededError } from "@/lib/validationSubmission";
import type {
  CompletionQuizStartError,
  CompletionQuizStartResult,
} from "@/types/courseCompletionQuiz";

/** The verdict of a submitted answer, as the quiz components consume it. */
export interface ValidationResult {
  correct: boolean | null;
  grade?: string;
  feedback: string;
  /**
   * Submissions the learner has used for this quiz slot, counted by the server.
   * Absent for quiz types whose verdict never travels through a validation
   * record.
   */
  attemptCount?: number;
  /**
   * How many submissions this quiz type allows, or null when it is unlimited.
   * The server owns this — local partial state is clearable, so a count kept
   * only in the browser cannot be trusted to gate retries.
   */
  maxAttempts?: number | null;
}

interface AnswerSubmission {
  enrollmentId: string;
  type: string;
  userInput: string;
  contentElementId: string;
  entryIndex: number;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const pollValidation = async (
  id: string,
  { initialIntervalMs = 150, maxIntervalMs = 1000, timeoutMs = 30000 } = {}
): Promise<Validation> => {
  const deadline = Date.now() + timeoutMs;
  // Start polling fast so deterministic validations (true/false, multiple
  // choice) — which the backend job resolves almost instantly — return in a
  // few hundred ms, then back off toward 1s so LLM-graded types (freetext,
  // code) don't hammer the server while they take longer.
  let intervalMs = initialIntervalMs;
  while (Date.now() < deadline) {
    // A failed poll is retried like a pending one; only the deadline gives up.
    const data = await getValidation(id).catch(() => null);
    if (data && data.status !== "pending") return data;
    await sleep(intervalMs);
    intervalMs = Math.min(intervalMs * 2, maxIntervalMs);
  }
  throw new Error("validation_timeout");
};

/**
 * Submits an answer and waits for the graded validation record. A refused
 * submission throws the HttpError; a failed grading its own error code.
 */
const submitAndAwaitValidation = async (
  params: AnswerSubmission
): Promise<Validation> => {
  const { id } = await createValidation({
    type: params.type as CreateValidationRequestType,
    userInput: params.userInput,
    contentElementId: params.contentElementId,
    entryIndex: params.entryIndex,
    enrollmentId: params.enrollmentId,
  });
  const data = await pollValidation(id);
  if (data.status === "failed") {
    throw new Error(data.result.error ?? "validation_failed");
  }
  return data;
};

const attemptsExceeded = (error: unknown): ValidationAttemptsExceeded | null =>
  error instanceof HttpError &&
  error.status === 422 &&
  errorCodeOf(error) === "attempts_exceeded"
    ? (error.body as ValidationAttemptsExceeded)
    : null;

/**
 * The calls that need more than one request or a decision on the response.
 * Plain reads and writes use the generated client directly.
 */
export const QuizValidationService = {
  /** Starts a run of the course completion quiz, or returns the open one. */
  async startCourseCompletionQuiz(
    enrollmentId: string
  ): Promise<CompletionQuizStartResult> {
    try {
      return { ok: true, run: await startCourseCompletionQuiz(enrollmentId) };
    } catch (error) {
      const code = errorCodeOf(error);
      if (error instanceof HttpError && error.status === 422 && code) {
        return { ok: false, error: code as CompletionQuizStartError };
      }
      throw new Error("Failed to start the course completion quiz");
    }
  },

  /**
   * Submits the answer to one question of the open run and waits until it is
   * graded. The verdict stays on the server until the quiz is over, so nothing
   * about right or wrong comes back.
   */
  async submitCourseCompletionQuizAnswer(
    params: AnswerSubmission
  ): Promise<void> {
    await submitAndAwaitValidation(params).catch((error: unknown) => {
      if (error instanceof HttpError) {
        throw new Error("Failed to submit the answer");
      }
      throw error;
    });
  },

  async validateAnswer(params: {
    type: string;
    userInput: string;
    contentElementId: string;
    entryIndex: number;
  }): Promise<ValidationResult> {
    const enrollmentId = useCourseDetailStore.getState().enrollmentId;
    if (!enrollmentId) throw new Error("Failed to validate answer");
    const data = await submitAndAwaitValidation({
      ...params,
      enrollmentId,
    }).catch((error: unknown) => {
      const refusal = attemptsExceeded(error);
      if (refusal) {
        throw new ValidationAttemptsExceededError(
          refusal.attemptCount ?? 0,
          refusal.maxAttempts ?? null
        );
      }
      if (error instanceof HttpError) {
        throw new Error("Failed to validate answer");
      }
      throw error;
    });
    return {
      correct: data.result.correct ?? null,
      grade: data.result.grade,
      feedback: data.result.feedback ?? "",
      attemptCount: data.attemptCount,
      maxAttempts: data.maxAttempts,
    };
  },
};
